import type { ReactElement } from 'react';
import { type Op, istEineGruppe, keyframeAround, keyframeAt, timelineDuration } from '@schnitt/shared';
import { useEditor } from '../store';
import { duplicateClip, pasteClips, splitAtPlayhead, toggleKeyframe } from '../edits';
import { newId } from '../ids';
import { useZeitText } from '../zeit';
import { Icon } from './Icon';
import { WerkzeugeSheet } from './WerkzeugeSheet';
import { ClipMehrSheet } from './ClipMehrSheet';
import { loescheClips } from '../loeschen';

export type SheetName =
  | 'media'
  | 'clip'
  | 'brand'
  | 'brandKit'
  | 'transcript'
  | 'export'
  | 'project'
  | 'teilen'
  | 'voiceover'
  | 'audio'
  | 'kommentare'
  | 'spur'
  | 'bereich'
  | 'markenAssistent'
  | 'verlauf'
  | 'uebergaenge'
  | 'messgeraete'
  | 'diashow'
  | 'werkzeuge'
  | 'ki'
  | 'mehr'
  | null;

/**
 * Die Quellenleiste am grossen Schirm (7.25c).
 *
 * Aus Saschas Entwurf: links die *Quellen* -- woher Material kommt --, unten
 * die *Werkzeuge*, mit denen man daran arbeitet. Auf dem Telefon gibt es
 * diese Trennung nicht, dort ist unten alles; deshalb steht die Leiste im
 * Stilblatt erst ab 1024 px, und die drei Knöpfe verschwinden dort unten aus
 * dem Werkzeugkasten. Zwei sichtbare Wege zum selben Blatt wären eine
 * Doppelung, und die hat er zu Recht schon einmal angestrichen.
 */
export function QuellenLeiste({ onOpen, active }: { onOpen: (sheet: SheetName) => void; active: SheetName }): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const leer = timelineDuration(timeline) === 0;
  return (
    <nav className="quellen" aria-label="Quellen">
      <Tool icon="plus" label="Hinzufügen" active={active === 'media'} highlight={leer} onClick={() => onOpen('media')} />
      <Tool icon="text" label="Text" active={active === 'brand'} onClick={() => onOpen('brand')} />
      <Tool icon="brand" label="Marke" active={active === 'brandKit'} onClick={() => onOpen('brandKit')} />
    </nav>
  );
}

/**
 * The row along the bottom, and the heart of the phone layout: it shows what
 * can be done *right now*. With nothing selected it adds things to the cut;
 * with a clip selected it turns into that clip's tools -- split, copy, delete,
 * mute, and the way into the detailed settings. One row that changes beats
 * five tabs that never do, because the second row would have to be scrolled
 * for and the fifth tab explained.
 */
export function Toolbar({
  onOpen,
  active,
  onBefehle,
}: {
  onOpen: (sheet: SheetName) => void;
  active: SheetName;
  onBefehle: () => void;
}): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const toast = useEditor((s) => s.toast);
  const uebergangsModus = useEditor((s) => s.uebergangsModus);
  const setUebergangsModus = useEditor((s) => s.setUebergangsModus);

  const empty = timelineDuration(timeline) === 0;
  /**
   * **Fünf Knöpfe, und keiner rollt aus dem Bild** (21. September). Sascha:
   * „Bitte räume das untere Menü auf, sodass wir im Schnitt immer nur so
   * fünf Buttons haben. Das muss irgendwie gehen und dann direkt kommen,
   * weil es ist sehr unbedienbar.“
   *
   * Am 18. September waren es neun, in Gruppen und mit einem Verlauf am
   * rechten Rand, damit man das Rollen sieht. Das war die falsche Antwort
   * auf dieselbe Frage: neun Kästchen auf einem Telefon sind neun Kästchen,
   * ob sie rollen oder nicht, und was hinter der Kante liegt, findet man
   * nicht, sondern man sucht es.
   *
   * Also fünf, immer dieselben fünf, immer an derselben Stelle: **woher das
   * Material kommt** (Medien, Text), **womit man schneidet** (Übergänge),
   * **alles Übrige** (Werkzeuge) und **wohin es hinausgeht** (Export).
   * Nichts ist weggefallen -- Marke, Untertitel, Messgeräte, Raster und
   * Suchen stehen im Blatt hinter „Werkzeuge“, mit einem Satz dazu, und ein
   * Satz ist leichter zu finden als ein Symbol.
   */
  return (
    <>
    {active === 'werkzeuge' ? (
      <WerkzeugeSheet onClose={() => onOpen(null)} onOpen={onOpen} onBefehle={onBefehle} />
    ) : null}
    <nav className="toolbar" aria-label="Werkzeuge" data-knoepfe="5">
        {/* Woher das Material kommt. Am grossen Schirm steht Medien links in
            der Quellenleiste und fällt hier weg (7.25c). */}
        {/* „Hinzufügen“ statt „Medien“ (17. Oktober): aussuchen, dann im Schnitt
            auf ein + tippen, wohin. */}
        <Tool icon="plus" label="Hinzufügen" quelle active={active === 'media'} highlight={empty} onClick={() => onOpen('media')} />
        <Tool icon="text" label="Text" quelle active={active === 'brand' || active === 'brandKit'} onClick={() => onOpen('brand')} />

        {/* 7.74e: Der Knopf öffnet kein Blatt, sondern macht die Schnitte
            gross -- an jedem steht dann ein +. (Sascha, 10. September: „Wir
            müssten vielleicht n kleines + haben zwischen zwei Tracks.“) */}
        <Tool
          icon="transition"
          label="Übergänge"
          active={uebergangsModus || active === 'uebergaenge'}
          onClick={() => {
            const an = !uebergangsModus;
            setUebergangsModus(an);
            // Das Blatt aus dem Weg: im Modus zählt die Zeitleiste.
            if (an) onOpen(null);
          }}
        />

        {/* Und alles, was seltener gebraucht wird, hinter *einem* Knopf:
            Marke, Untertitel, Messgeräte, Raster, Suchen. Was dort liegt,
            steht im Blatt untereinander mit einem Satz dazu -- lesbar, statt
            als zehntes Kästchen am Rand abgeschnitten. */}
        <Tool icon="sliders" label="Werkzeuge" active={active === 'werkzeuge'} onClick={() => onOpen('werkzeuge')} />

        {/* Nur noch der Export: „Zusammenarbeiten“ steht seit dem 27. Oktober oben
            bei den Köpfen, mit eigenem Blatt (Sascha: „so verschachtelt und viel Text“). */}
        <Tool icon="export" label="Export" active={active === 'export'} onClick={() => onOpen('export')} />

        {/* Die Plätze aus Saschas Entwurf, die es noch nicht gibt. Sie stehen
            **nur am grossen Schirm** (`spaeter` blendet sie auf dem Telefon
            aus): dort ist Platz, und man sieht, wo ein Werkzeug später sein
            wird. Unten auf dem Telefon wären sie der sechste und siebte
            Knopf -- und genau darum geht es hier nicht. */}
        <Tool icon="transition" label="Effekte" className="spaeter" onClick={() => toast('info', 'Effekte stehen beim Clip unter „Bearbeiten“.')} />
        <Tool icon="brand" label="Elemente" className="spaeter" onClick={() => toast('info', 'Elemente (Formen, Sticker, Pfeile) sind noch nicht gebaut.')} />
    </nav>
    </>
  );
}

/**
 * Die Werkzeuge für den gewählten Clip. Sie stehen unten und treten an die
 * Stelle der Projektwerkzeuge, solange etwas gewählt ist.
 *
 * Eine Zeit lang hatten sie eine eigene Reihe unter der Vorschau, damit man
 * nebenbei an *Medien* kommt. Der Preis war eine Reihe mehr, und die ging der
 * Leinwand ab -- auf dem Telefon das Knappste, was es gibt. Jetzt ist es
 * wieder eine Leiste, und der erste Knopf gibt die andere zurück.
 *
 * Elf Knöpfe nebeneinander sind aber auch eine Leiste, die man lesen muss.
 * Deshalb haben verwandte Knöpfe einen Elternknopf: *Keyframe* und *Ton*
 * stehen für sich, und erst ein Tipp klappt ihre Kinder daneben auf (Sascha,
 * 6. September). Aufgeklappt kommt, was der Clip schon benutzt -- wer
 * Keyframes gesetzt hat, findet sie beim nächsten Antippen offen vor.
 */
export function ClipTools({ onOpen, active }: { onOpen: (sheet: SheetName) => void; active: SheetName }): ReactElement | null {
  const timeline = useEditor((s) => s.timeline);
  const selection = useEditor((s) => s.selection);
  const select = useEditor((s) => s.select);
  const dispatch = useEditor((s) => s.dispatch);
  const playhead = useEditor((s) => s.playhead);
  const clipById = useEditor((s) => s.clipById);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);
  const multiSelect = useEditor((s) => s.multiSelect);
  const setMultiSelect = useEditor((s) => s.setMultiSelect);
  const nachrueckenAn = useEditor((s) => s.nachruecken);
  const nachrueckenUmschalten = useEditor((s) => s.nachrueckenUmschalten);
  const copySelection = useEditor((s) => s.copySelection);
  const setPlayhead = useEditor((s) => s.setPlayhead);

  const editable = role !== 'viewer';
  const one = selection.length === 1 ? clipById(selection[0]!) : null;
  const clipId = one?.clip.id ?? null;

  /**
   * Keine aufklappbaren Gruppen mehr (21. September): sie waren der Grund,
   * warum die Leiste unter dem Finger wanderte. Was sie enthielten, steht im
   * Blatt „Mehr“. Damit fällt auch das Nachrollen weg, das eine aufgeklappte
   * Gruppe ins Bild schieben musste.
   */
  if (selection.length === 0 || !editable) return null;

  const split = () => {
    const ops = splitAtPlayhead(timeline, playhead, selection);
    if (ops.length === 0) {
      toast('info', 'Erst den Abspielkopf auf den Clip stellen.');
      return;
    }
    dispatch(ops);
  };

  const remove = () => {
    loescheClips(selection);
  };

  const duplicate = () => {
    if (!one) return;
    const copy = duplicateClip(timeline, one.clip.id);
    if (!copy) return;
    dispatch(copy.op);
    select([copy.id]);
  };

  // Gruppieren und lösen (S3). Die Kennung steht am Clip; eine Gruppe
  // anzulegen heisst, allen Gewählten dieselbe zu geben, und sie zu lösen
  // heisst, sie allen wegzunehmen.
  const gruppiert = istEineGruppe(timeline, selection);
  const gruppieren = () => {
    const id = newId('g');
    dispatch(selection.map((clipId) => ({ op: 'updateClip', clipId, patch: { gruppe: id } }) as Op));
    toast('success', `${selection.length} Clips gruppiert — sie bewegen sich jetzt zusammen.`);
  };
  const gruppeLoesen = () => {
    dispatch(selection.map((clipId) => ({ op: 'updateClip', clipId, patch: { gruppe: null } }) as Op));
    toast('info', 'Gruppe gelöst.');
  };

  const isText = one?.clip.source.kind === 'text';
  const hasSound = one?.clip.source.kind === 'asset';
  const track = one ? timeline.tracks.find((t) => t.id === one.trackId) : null;
  const fps = timeline.settings.fps;
  const local = one ? Math.min(one.clip.duration, Math.max(0, playhead - one.clip.start)) : 0;
  const aufKeyframe = one ? keyframeAt(one.clip, local, 1 / fps) !== null : false;

  const keyframe = () => {
    if (!one) return;
    const { op, gesetzt } = toggleKeyframe(one.clip, local, fps);
    dispatch(op);
    toast('success', gesetzt ? 'Keyframe gesetzt.' : 'Keyframe entfernt.');
  };

  const springeKeyframe = (richtung: 1 | -1) => {
    if (!one) return;
    const ziel = keyframeAround(one.clip, local, richtung);
    if (ziel === null) {
      toast('info', richtung === 1 ? 'Danach kommt kein Keyframe mehr.' : 'Davor liegt kein Keyframe.');
      return;
    }
    setPlayhead(one.clip.start + ziel);
  };

  const alleKeyframesWeg = () => {
    if (!one) return;
    dispatch({ op: 'updateClip', clipId: one.clip.id, patch: { keyframes: [] } } as Op);
    toast('success', 'Alle Keyframes entfernt.');
  };

  const stumm = () => {
    if (!one) return;
    dispatch({ op: 'updateClip', clipId: one.clip.id, patch: { muted: !one.clip.muted } } as Op);
  };

  const count = selection.length;
  const kopieren = () => {
    copySelection();
    toast('success', count > 1 ? `${count} Clips kopiert.` : 'Clip kopiert.');
  };
  /**
   * **Die häufigen Sachen in die Reihe, und die Reihe rollt** (Sascha,
   * 12. Oktober: „unter mehr bräuchten wir ein anderes icon. Vielleicht
   * können wir den auch auslassen und ein paar noch in die Reihe scrollbar
   * einfügen am Handy“). Vom 21. September an standen hier fünf Knöpfe, und
   * alles andere -- auch Keyframe, Stumm, Kopieren -- lag hinter „Mehr“, mit
   * dem Zeichen fürs Kopieren davor.
   *
   * Jetzt: **Zurück** links und **Löschen** rechts stehen fest, dazwischen
   * rollt die Reihe -- Trennen, Bearbeiten, Keyframe (Video), Stumm (mit Ton),
   * Duplizieren, Kopieren, und am Ende „Mehr“ (⋯) für das Seltene:
   * Keyframes anspringen, Pegel, Nachrücken, mehrere wählen, Gruppen.
   * Aufklappen tut sich nichts mehr; ein Knopf, der da ist, bleibt an seiner
   * Stelle, solange derselbe Clip gewählt ist.
   */
  return (
    <>
    {active === 'mehr' ? (
      <ClipMehrSheet
        onClose={() => onOpen(null)}
        onOpen={onOpen}
        eines={one ?? null}
        spurArt={track?.kind ?? null}
        anzahl={count}
        gruppiert={gruppiert}
        keyframes={one?.clip.keyframes.length ?? 0}
        pegelpunkte={one?.clip.gainKeyframes?.length ?? 0}
        nachrueckenAn={nachrueckenAn}
        mehrfach={multiSelect}
        istText={isText}
        hatTon={hasSound}
        tun={{
          springeKeyframe,
          alleKeyframesWeg,
          nachruecken: () => {
            nachrueckenUmschalten();
            toast('info', nachrueckenAn
              ? 'Nachrücken aus: Kürzen lässt eine Lücke stehen.'
              : 'Nachrücken an: Kürzen zieht alles dahinter nach.');
          },
          mehrfachUmschalten: () => setMultiSelect(!multiSelect),
          gruppieren,
          gruppeLoesen,
        }}
      />
    ) : null}
    <nav className="toolbar context cliptools" aria-label="Clip-Werkzeuge">
      <Tool icon="back" label={count > 1 ? `Zurück (${count})` : 'Zurück'} className="back" onClick={() => select([])} />
      <Tool icon="scissors" label="Trennen" onClick={split} />
      {/* Der dritte Platz gehört dem, was an *diesem* Clip zu tun ist -- und
          bei mehreren dem, was nur mit mehreren geht. */}
      {one ? (
        <Tool icon="sliders" label="Bearbeiten" active={active === 'clip'} onClick={() => onOpen('clip')} />
      ) : gruppiert ? (
        <Tool icon="ungroup" label="Gruppe lösen" onClick={gruppeLoesen} />
      ) : (
        <Tool icon="group" label={`${count} gruppieren`} onClick={gruppieren} />
      )}
      {one && track?.kind === 'video' ? (
        <Tool
          icon="keyframe"
          label={aufKeyframe ? 'Keyframe weg' : 'Keyframe'}
          badge={one.clip.keyframes.length || undefined}
          active={aufKeyframe}
          onClick={keyframe}
        />
      ) : null}
      {one && hasSound ? (
        <Tool icon={one.clip.muted ? 'mute' : 'volume'} label={one.clip.muted ? 'Ton an' : 'Stumm'} active={one.clip.muted} onClick={stumm} />
      ) : null}
      {one ? <Tool icon="copy" label="Duplizieren" onClick={duplicate} /> : null}
      <Tool icon="clipboard" label="Kopieren" onClick={kopieren} />
      <Tool icon="mehr" label="Mehr" active={active === 'mehr'} onClick={() => onOpen('mehr')} />
      <Tool icon="trash" label="Löschen" className="danger loeschen" onClick={remove} />
    </nav>
    </>
  );
}

function Tool({
  icon,
  label,
  onClick,
  active = false,
  highlight = false,
  badge,
  className = '',
  quelle = false,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  active?: boolean;
  highlight?: boolean;
  /** Eine der drei Quellen: am grossen Schirm steht sie links, nicht unten (7.25c). */
  quelle?: boolean;
  /** Eine Zahl am Symbol statt in der Beschriftung. „Keyframes (3)“ wurde so
   *  breit, dass die Schrift in den Nachbarknopf lief. */
  badge?: number;
  className?: string;
}): ReactElement {
  return (
    <button
      className={`${className} ${active ? 'active' : ''} ${highlight ? 'highlight' : ''}`.trim()}
      data-quelle={quelle ? 'ja' : undefined}
      onClick={onClick}
      aria-label={badge ? `${label} (${badge})` : label}
      aria-pressed={active || undefined}
    >
      <span className="tool-symbol">
        <Icon name={icon} />
        {badge ? <span className="zaehler">{badge}</span> : null}
      </span>
      <span>{label}</span>
    </button>
  );
}


/**
 * Shown while something is on the clipboard: one tap pastes at the playhead.
 * A bar above the tools rather than a seventh tool, so the main row still
 * fits a phone -- and it says where the paste will go.
 */
export function PasteBar(): ReactElement | null {
  const zeit = useZeitText();
  const clipboard = useEditor((s) => s.clipboard);
  const clearClipboard = useEditor((s) => s.clearClipboard);
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead);
  const dispatch = useEditor((s) => s.dispatch);
  const select = useEditor((s) => s.select);
  const role = useEditor((s) => s.role);
  if (clipboard.length === 0 || role === 'viewer') return null;
  const fps = timeline.settings.fps;
  return (
    <div className="paste-bar" role="status">
      <button
        className="primary small"
        onClick={() => {
          const { ops, ids } = pasteClips(timeline, clipboard, playhead);
          if (ops.length === 0) return;
          dispatch(ops);
          select(ids);
        }}
      >
        {clipboard.length > 1 ? `${clipboard.length} Clips` : 'Clip'} einfügen bei {zeit(playhead, true)}
      </button>
      <button className="ghost small" onClick={clearClipboard} aria-label="Zwischenablage leeren">
        ✕
      </button>
    </div>
  );
}
